import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ActionStat } from "./FlowReport";
import styles from "./FlowReport.module.css";

interface ActionStatsChartProps {
  stats: ActionStat[];
  beautifiedNames?: Record<string, string>;
  xaxistitle?: string;
  /** Show pending count in chart (default: true) */
  showPending?: boolean;
}

/**
 * ActionStatsChart Component
 * 
 * Displays a VERTICAL stacked bar chart showing success/failure/pending breakdown
 * for each workflow action.
 */
const ActionStatsChart: React.FC<ActionStatsChartProps> = ({ 
  stats, 
  beautifiedNames = {},
  xaxistitle = "",
  showPending = true
}) => {

  const getDisplayName = (stat: ActionStat): string => {
    if (stat.displayName) {
      return stat.displayName;
    }
    if (beautifiedNames[stat.actionName]) {
      return beautifiedNames[stat.actionName];
    }
    return stat.actionName;
  };

  const categories = useMemo(() => 
    stats.map((s) => getDisplayName(s)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stats, beautifiedNames]
  );

  /**
   * Wrap long text into multiple lines
   * @param text - The text to wrap
   * @param maxCharsPerLine - Maximum characters per line
   * @returns HTML string with <br/> for line breaks
   */
  const wrapLabel = (text: string, maxCharsPerLine: number = 20): string => {
    if (!text || text.length <= maxCharsPerLine) {
      return text;
    }

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      if (currentLine.length + word.length + 1 <= maxCharsPerLine) {
        currentLine = currentLine ? `${currentLine} ${word}` : word;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        // Handle very long words
          currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.join('<br/>');
  };

  const options: Highcharts.Options = useMemo(() => {
    // Build series based on showPending flag
    const series: Highcharts.SeriesOptionsType[] = [
      {
        type: "column",
        name: "Success",
        data: stats.map((s) => s.successCount),
        color: "#00C25F", // Green
      },
      {
        type: "column",
        name: "Failed",
        data: stats.map((s) => s.failCount),
        color: "#ef4444", // Red
      },
    ];

    // Add Pending series if enabled and there are pending items
    if (showPending) {
      const hasPending = stats.some((s) => (s.pendingCount || 0) > 0);
      if (hasPending) {
        series.push({
          type: "column",
          name: "Pending",
          data: stats.map((s) => s.pendingCount || 0),
          color: "#E5EA0A",
        });
      }
    }

    // Determine if we need more height for labels
    const hasLongLabels = categories.some((c) => c.length > 20);
    const chartHeight = hasLongLabels ? 320 : 275;

    return {
      chart: {
        type: "column",
        height: chartHeight,
        backgroundColor: "transparent",
        style: {
          fontFamily: "inherit",
        },
        marginBottom: hasLongLabels ? 90 : 60,
      },

      title: {
        text: "",
      },

      xAxis: {
        categories,
        title: {
          text: xaxistitle,
          style: {
            fontWeight: "600",
            color: "#374151",
          },
        },
        labels: {
          useHTML: true,
          rotation: 0,
          style: {
            fontSize: "11px",
            color: "#4b5563",
            textAlign: "center",
            whiteSpace: "normal",
          },
          formatter: function () {
            const label = String(this.value);
            return `<div style="text-align: center; line-height: 1.3; white-space: nowrap;">${wrapLabel(label, 20)}</div>`;
          },
        },
        crosshair: true,
      },

      yAxis: {
        min: 0,
        title: {
          text: "Count",
          style: {
            fontWeight: "600",
            color: "#374151",
          },
        },
        labels: {
          style: {
            fontSize: "11px",
            color: "#4b5563",
          },
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            color: "#374151",
            textOutline: "1px white",
          },
          formatter: function () {
            return this.total ? String(this.total) : "";
          },
        },
        gridLineColor: "#e5e7eb",
      },

      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: "#374151",
          fontWeight: "500",
        },
        itemHoverStyle: {
          color: "#111827",
        },
      },

      tooltip: {
        shared: true,
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        borderRadius: 8,
        shadow: true,
        style: {
          fontSize: "12px",
        },
        formatter: function () {
          const points = (this as any).points ?? [];
          const categoryIndex = points[0]?.point?.index ?? 0;
          const row = stats[categoryIndex];

          if (!row) return "";

          const displayName = getDisplayName(row);
          const success = points.find((p: any) => p.series.name === "Success")?.y ?? 0;
          const failed = points.find((p: any) => p.series.name === "Failed")?.y ?? 0;
          const pending = points.find((p: any) => p.series.name === "Pending")?.y ?? 0;

          // Calculate completed (non-pending)
          const completed = success + failed;
          
          // Success rate based on completed attempts
          const successRate = completed > 0 ? (success / completed * 100) : 0;
          
          // Determine success rate color
          const rateColor = successRate >= 90 ? '#00C25F' :  // New Green
                 successRate >= 70 ? '#E5EA0A' :   // New Yellow
                 '#ef4444';                         // Red

          // Build pending line conditionally
          const pendingLine = showPending && pending > 0 
            ? `<span style="color: #E5EA0A;">● Pending:</span> <strong>${pending}</strong><br/>`
            : '';

          return `
            <div style="padding: 4px 0;">
              <strong style="font-size: 13px; color: #111827;">${displayName}</strong>
              <br/><br/>
              <span style="color: #6b7280;">Total Records:</span> <strong>${row.totalAttempts}</strong><br/>
              <span style="color: #00C25F;">● Success:</span> <strong>${success}</strong><br/>
              <span style="color: #ef4444;">● Failed:</span> <strong>${failed}</strong><br/>
              ${pendingLine}
              <br/>
              <span style="color: #6b7280;">Completion Rate:</span> <strong>${((completed / row.totalAttempts) * 100).toFixed(1)}%</strong><br/>
              <span style="color: #6b7280;">Success Rate:</span> <strong style="color: ${rateColor};">${successRate.toFixed(1)}%</strong>
              <span style="color: #9ca3af; font-size: 10px;">(of completed)</span>
            </div>
          `;
        },
        useHTML: true,
      },

      plotOptions: {
        column: {
          stacking: "normal",
          borderRadius: 4,
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.15,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const val = this.y;
              return val && val > 0 ? val : null;
            },
            style: {
              fontSize: "11px",
              fontWeight: "600",
              textOutline: "2px white",
              color: "#374151",
            },
          },
        },
      },

      series,

      credits: {
        enabled: false,
      },

      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                align: "center",
                verticalAlign: "bottom",
                layout: "horizontal",
              },
              chart: {
                marginBottom: 100,
              },
              xAxis: {
                labels: {
                  rotation: -45,
                  style: {
                    fontSize: "10px",
                  },
                  // formatter: function () {
                  //   const label = String(this.value);
                  //   // On small screens, truncate instead of wrap
                  //   return label.length > 15 ? label.slice(0, 15) + '...' : label;
                  // },
                },
              },
            },
          },
        ],
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, stats, beautifiedNames, showPending, xaxistitle]);

  if (!stats?.length) return null;

  return (
    <div className={styles.highchartsWrap}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default ActionStatsChart;